import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { CURSIVE_FONTS, DEFAULT_TEXT_STYLE, type TextStyle } from './types';

interface TextElementDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: (text: string, style: TextStyle) => void;
  initialText?: string;
  title?: string;
}

const QUICK_SUGGESTIONS = [
  'Lu et approuvé',
  'Bon pour accord',
  'Pour accord',
  'Certifié conforme',
];

export function TextElementDialog({
  open,
  onOpenChange,
  onConfirm,
  initialText = '',
  title = 'Ajouter du texte',
}: TextElementDialogProps) {
  const [text, setText] = useState(initialText);
  const [style, setStyle] = useState<TextStyle>(DEFAULT_TEXT_STYLE);

  const handleConfirm = () => {
    if (text.trim()) {
      onConfirm(text.trim(), style);
      setText('');
      setStyle(DEFAULT_TEXT_STYLE);
      onOpenChange(false);
    }
  };

  const handleSuggestion = (suggestion: string) => {
    setText(suggestion);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Font & Size selectors */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Police</Label>
              <Select
                value={style.fontFamily}
                onValueChange={(value) => setStyle({ ...style, fontFamily: value as TextStyle['fontFamily'] })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURSIVE_FONTS.map((font) => (
                    <SelectItem key={font.value} value={font.value}>
                      <span style={{ fontFamily: font.value }}>{font.label}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Taille</Label>
              <Select
                value={String(style.fontSize)}
                onValueChange={(value) => setStyle({ ...style, fontSize: Number(value) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[16, 20, 24, 28, 32, 36, 40].map((size) => (
                    <SelectItem key={size} value={String(size)}>
                      {size}px
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Color */}
          <div className="space-y-2">
            <Label>Couleur</Label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={style.color}
                onChange={(e) => setStyle({ ...style, color: e.target.value })}
                className="w-10 h-10 rounded border border-border cursor-pointer"
              />
              <Input
                value={style.color}
                onChange={(e) => setStyle({ ...style, color: e.target.value })}
                className="flex-1"
              />
            </div>
          </div>

          {/* Text input with preview */}
          <div className="space-y-2">
            <Label>Votre texte</Label>
            <div
              className="min-h-[60px] p-3 border border-border rounded-lg bg-white"
              style={{
                fontFamily: style.fontFamily,
                fontSize: `${style.fontSize}px`,
                color: style.color,
              }}
            >
              <Input
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="Entrez votre texte..."
                className="border-0 p-0 h-auto bg-transparent focus-visible:ring-0"
                style={{
                  fontFamily: style.fontFamily,
                  fontSize: `${style.fontSize}px`,
                  color: style.color,
                }}
              />
            </div>
          </div>

          {/* Quick suggestions */}
          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs">Suggestions rapides</Label>
            <div className="flex flex-wrap gap-2">
              {QUICK_SUGGESTIONS.map((suggestion) => (
                <Button
                  key={suggestion}
                  variant="outline"
                  size="sm"
                  onClick={() => handleSuggestion(suggestion)}
                  className="text-xs"
                >
                  {suggestion}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Annuler
          </Button>
          <Button onClick={handleConfirm} disabled={!text.trim()}>
            Ajouter
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
